import React from "react";
import { UserWord, UserWordStatus } from "@shared_types";
import { FaPlus, FaTrash, FaTimesCircle, FaCheckCircle } from "react-icons/fa";
import ToggleButton from "../Buttons/ToggleButton";
import BaseWordCard from "../BaseWordCard";

interface WordCardProps {
  word: UserWord;
  viewType: "known" | "learning";
  effectiveStatus: UserWordStatus;
  deleteConfirmId: number | string | null;
  onStatusChange: (word: UserWord, newStatus: UserWordStatus) => void;
  onDeleteClick: (wordId: number) => void;
  onCancelDelete: () => void;
  lastWordRef?: React.Ref<HTMLDivElement>;
}

const GREEN = "text-[var(--color-green)]";
// iOS StatusToggleButton renders the unselected/action state in muted grey
// (icon globalColors.grey, text "muted"). Mirror that with the muted-grey token
// so "+ to learning" / "+ I know this" read grey, not near-black.
const MUTED = "text-[var(--color-grey-other)]";

const WordCard: React.FC<WordCardProps> = ({
  word,
  viewType,
  effectiveStatus,
  deleteConfirmId,
  onStatusChange,
  onDeleteClick,
  onCancelDelete,
  lastWordRef,
}) => {
  // iOS StatusToggleButton: the action you CAN take shows unselected; the
  // resulting status shows selected (green check).
  // isActive === (effectiveStatus === "known").
  let activeIcon: React.ReactNode;
  let inactiveIcon: React.ReactNode;
  let activeText: string;
  let inactiveText: string;
  let activeColor: string;
  let inactiveColor: string;

  if (viewType === "learning") {
    // Learning view: unselected "I know this" (action) → selected "known".
    inactiveText = "I know this";
    inactiveIcon = <FaPlus className="w-3 h-3" />;
    inactiveColor = MUTED;
    activeText = "known";
    activeIcon = <FaCheckCircle className="w-3 h-3" />;
    activeColor = GREEN;
  } else {
    // Known view: unselected "to learning" (action) → selected "learning".
    activeText = "to learning";
    activeIcon = <FaPlus className="w-3 h-3" />;
    activeColor = MUTED;
    inactiveText = "learning";
    inactiveIcon = <FaCheckCircle className="w-3 h-3" />;
    inactiveColor = GREEN;
  }

  const actions = (
    <>
      <ToggleButton
        isActive={effectiveStatus === "known"}
        activeText={activeText}
        inactiveText={inactiveText}
        activeColor={activeColor}
        activeIcon={activeIcon}
        inactiveColor={inactiveColor}
        inactiveIcon={inactiveIcon}
        disableAnimation={true}
        onClick={() =>
          onStatusChange(
            word,
            effectiveStatus === "known" ? "learning" : "known"
          )
        }
        className={`text-xs px-2 py-1 rounded-full transition-colors`}
      />
      {deleteConfirmId === word.userWordId ? (
        <div className="flex gap-1">
          <button
            onClick={() => onDeleteClick(word.userWordId)}
            className="text-xs text-white bg-[var(--color-red)] border border-[var(--color-red)] px-2 py-1 rounded-full transition-colors flex items-center"
          >
            <FaCheckCircle className="w-3 h-3" />
          </button>
          <button
            onClick={onCancelDelete}
            className="text-xs text-[var(--color-grey)] border border-[var(--color-grey-light)] px-2 py-1 rounded-full transition-colors flex items-center"
          >
            <FaTimesCircle className="w-3 h-3" />
          </button>
        </div>
      ) : (
        <button
          onClick={() => onDeleteClick(word.userWordId)}
          className="text-xs border border-[var(--color-grey-light)] px-2 py-1 rounded-full transition-colors flex items-center"
        >
          <FaTrash className="w-3 h-3" />
        </button>
      )}
    </>
  );

  return (
    <BaseWordCard
      targetWord={word.targetWord}
      nativeWord={word.nativeWord}
      effectiveStatus={effectiveStatus}
      actions={actions}
      lastWordRef={lastWordRef}
      layout="row"
      highlight={false}
    />
  );
};

export default React.memo(WordCard);
